import { model as i18nModel } from '!/i18n'
import { model as router } from '!/router'
import { model as popupModel } from '$/info-popup'
import { noop } from '@/effector'
import { aye, nay } from '@/helpers'
import { api, type Catchup, type CatchupProgram } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { persist } from 'effector-storage/session'
import { reset } from 'patronum'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
} from '~/shared/interfaces'
import { type CatchupsRedirectParams } from '~/shared/interfaces'
import {
  formatCatchupsDate,
  formatPaidCatchup,
  formatProgramsForSelectedDate,
  lockedFormat,
} from './lib'

// all catchups
const DEFAULT_UNLOCK_ID = 0

export const navigateCatchupPlayer = createEvent<{
  catchupId: number
  channelId: number
  programmeId: number
}>()

sample({
  clock: navigateCatchupPlayer,
  fn: ({ catchupId, channelId, programmeId }) =>
    `/player/catchup?catchupId=${catchupId}&channelId=${channelId}&programmeId=${programmeId}`,
  target: router.navigatePush,
})

export const getAllCatchupsFx = //
  attach({ effect: api.catchups.getChannelsWithCatchupsFx })

export const $allCatchups = combine(
  api.catchups.$channelsWithCatchups,
  ssePurchasesModel.$channel,
  (channels, adjust) =>
    channels.map((catchup) => ({
      ...catchup,
      channel: adjust(catchup.channel),
    }))
)

export const setAllCatchupsToDisplay = createEvent<Catchup[]>()
export const $allCatchupsToDisplay = createStore<Catchup[]>([])
  .on(api.catchups.$channelsWithCatchups, (_, catchups) => catchups)
  .on(setAllCatchupsToDisplay, (_, catchups) => catchups)

export const $allCatchupsToDisplayFormatted = createStore<CatchupFormatted[]>(
  []
).on(
  combine([$allCatchupsToDisplay, ssePurchasesModel.$channel, i18nModel.$t]),
  (_, [catchups, adjust]) =>
    catchups.map((catchup) => formatPaidCatchup(catchup, adjust))
)

export const getChannelCatchupProgramsFx = //
  attach({ effect: api.catchups.getChannelCatchupProgramsFx })

export const $activeChannelCatchupsPrograms = //
  api.catchups.$selectedChannelCatchupPrograms

export const $activeChannelCatchupsDatelist = combine(
  api.catchups.$selectedChannelDateList,
  i18nModel.$t,
  (channelDateList, { t }) =>
    channelDateList.map((channelDate) => formatCatchupsDate(channelDate, t))
)

reset({
  clock: [api.session.signInFx.done, api.session.signOutFx.done],
  target: api.catchups.$channelsWithCatchups,
})

// active catchup

export const setActiveCatchup = createEvent<Catchup>()

export const $activeCatchup = createStore<Catchup | null>(null) //
  .on(setActiveCatchup, (_, catchup) => catchup)
  .reset(api.session.signInFx, api.session.signOutFx)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $activeCatchup,
  key: 'tria__active-catchup',
  fail: noop,
})

export const $seletedCatchupIndex = createStore<number | null>(null)

sample({
  clock: $activeCatchup,
  source: $allCatchupsToDisplayFormatted,
  fn: (catchups, catchup) =>
    catchup && catchups.findIndex((c) => c.id === catchup.id),
  target: $seletedCatchupIndex,
})
sample({
  clock: $activeCatchup,
  filter: (catchup) =>
    aye(catchup?.channel.priceSettings) &&
    catchup.channel.priceSettings.length > 0 &&
    aye(catchup.channel.priceSettings[0].bundle),
  fn: (catchup) => {
    return {
      bundleId: catchup?.channel.priceSettings![0].bundle!.id!,
      page: 0,
    }
  },
  target: api.tvBundles.loadTvBundleWithContentFx,
})
// active date

export const setActiveDate = createEvent<number>()

export const $activeDate = createStore<number | null>(null) //
  .on(setActiveDate, (_, date) => date)

// catchup program for active date

export const setCatchupProgramsForSelectedDate = //
  createEvent<{ programs: CatchupProgram[]; catchup: Catchup }>()

export const $catchupsProgramsForSelectedDate = createStore<CatchupProgram[]>(
  []
).on(setCatchupProgramsForSelectedDate, (_, { programs, catchup }) =>
  formatProgramsForSelectedDate({ programs, catchup })
)

// get catchup programs on catchup change
sample({
  clock: $activeCatchup,
  filter: Boolean,
  fn: (catchup) => ({
    catchupId: catchup.id,
    channelId: catchup.channel.id,
  }),
  target: getChannelCatchupProgramsFx,
})

// set catchup program for active date
sample({
  clock: combine({
    programs: $activeChannelCatchupsPrograms,
    date: $activeDate,
    i18n: i18nModel.$t,
  }),
  source: $activeCatchup,
  filter: (catchup, { programs, date }) =>
    aye(catchup) && aye(date) && aye(programs),
  fn: (catchup, { programs, date }) => ({
    programs: programs[date!],
    catchup: catchup!,
  }),
  target: setCatchupProgramsForSelectedDate,
})

// select catchup program for play

export const selectCathchupProgram = createEvent<{
  catchup: CatchupFormatted
  program: CatchupProgramFormatted
}>()

export const $selectedProgram = createStore<CatchupProgramFormatted | null>(
  null
).on(selectCathchupProgram, (_, { program }) => program)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $selectedProgram,
  key: 'tria__selected-program',
  fail: noop,
})

sample({
  clock: selectCathchupProgram,
  filter: ({ catchup }) =>
    aye(catchup.channel.free) || aye(catchup.channel.purchaseInfo),
  fn: ({ catchup, program }) => ({
    channelId: catchup.channel.id,
    catchupId: catchup.id,
    programmeId: program.id,
  }),
  target: navigateCatchupPlayer,
})

//empty programs

export const setIsCatchupsEmpty = createEvent<boolean>()

export const $isCatchupsEmpty = restore<boolean>(setIsCatchupsEmpty, false)

sample({
  clock: getChannelCatchupProgramsFx.doneData,
  source: $activeCatchup,
  fn: (activeCatchup, clockData) =>
    !clockData.dateList.length && !activeCatchup?.channel.locked,
  target: setIsCatchupsEmpty,
})

sample({
  clock: getChannelCatchupProgramsFx.fail,
  source: $activeCatchup,
  fn: (activeCatchup) => !activeCatchup?.channel.locked,
  target: setIsCatchupsEmpty,
})

// redirect to unlocked channel

export const setActiveUnlockedCatchups = createEvent<CatchupsRedirectParams>()

export const updateStateCatchups = createEvent<void>()

export const callCatchupsPinLockPopup = createEvent<{
  name: string
  locked: boolean
  id: number
}>()

export const $redirectToUnlockedCatchup = createStore<CatchupsRedirectParams>({
  catchupsId: 0,
  channelId: 0,
}).on(setActiveUnlockedCatchups, (_, payload) => payload)

export const $unlock = createStore(DEFAULT_UNLOCK_ID).on(
  setActiveUnlockedCatchups,
  (_, payload) => payload.channelId
)

sample({
  clock: setActiveUnlockedCatchups,
  source: $allCatchupsToDisplayFormatted,
  filter: (allCatchups, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )
    return aye(activeChannel?.channel.locked)
  },
  fn: (allCatchups, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )
    return {
      name: activeChannel!.channel.name,
      locked: activeChannel!.channel.locked,
      id: activeChannel!.channel.id,
    }
  },
  target: callCatchupsPinLockPopup,
})

sample({
  clock: popupModel.$unlockedId,
  source: $redirectToUnlockedCatchup,
  filter: ({ channelId, catchupsId }, unlockId) =>
    Boolean(channelId) && Boolean(catchupsId) && unlockId === channelId,
  target: updateStateCatchups,
})

sample({
  clock: updateStateCatchups,
  source: {
    allCatchups: $allCatchupsToDisplayFormatted,
    redirect: $redirectToUnlockedCatchup,
    unlockId: $unlock,
    defaultStore: $allCatchups,
  },
  fn: ({ allCatchups, redirect, unlockId, defaultStore }) =>
    lockedFormat({ allCatchups, redirect, unlockId, defaultStore }),
  target: $allCatchupsToDisplayFormatted,
})

sample({
  clock: setActiveUnlockedCatchups,
  source: $allCatchupsToDisplayFormatted,
  filter: (allCatchups, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )
    return nay(activeChannel?.channel.locked)
  },
  target: updateStateCatchups,
})

sample({
  clock: updateStateCatchups,
  source: $redirectToUnlockedCatchup,
  filter: ({ channelId, catchupsId }) =>
    Boolean(channelId) && Boolean(catchupsId),
  fn: ({ catchupsId, channelId }) =>
    `/catchups/${catchupsId}/channel/${channelId}`,
  target: router.navigatePush,
})
